/**
 * "Create sticker": pick, paste or reuse a message image → (static images) crop to a square, optionally remove the
 * background → name + keywords → an existing pack I can edit, or a new one → save. The platform makes the 512×512 WebP
 * (StickerImageProcessor); the bytes go through the regular upload queue (`DataSource.uploadFile`, target "sticker"),
 * then the sticker is created on the server. Online only (it says so when offline).
 */
import type { StickerPack, StickerPackScope } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { canEditPack, normalizeKeywords, packNameProblem, stickerFileProblem, stickerNameProblem } from "../logic/stickers.ts";
import { errorKind, errorMessage, type CropArea, type PickedFile } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { StickersViewModel } from "./StickersViewModel.ts";

export type PackChoice = { kind: "existing"; packId: string } | { kind: "new"; name: string; scope: StickerPackScope };

export type StickerCreatorState = {
  open: boolean;
  step: "pick" | "edit" | "saving";
  /** Reading the picked image. */
  inspecting: boolean;
  previewUrl?: string;
  width: number;
  height: number;
  /** Animated originals are kept as they are (no crop / background removal). */
  animated: boolean;
  crop?: CropArea;
  removeBackground: boolean;
  canRemoveBackground: boolean;
  name: string;
  keywords: string;
  /** Packs I may add stickers to. */
  packs: StickerPack[];
  choice: PackChoice;
  /** Admins may create a workspace pack right here. */
  canCreateWorkspacePack: boolean;
  allowed: boolean;
  error?: MessageDescriptor;
  canSave: boolean;
};

export class StickerCreatorViewModel extends ViewModel<StickerCreatorState> {
  private readonly stickers: StickersViewModel;
  private file: PickedFile | undefined;
  private generation = 0;

  constructor(stickers: StickersViewModel) {
    super({
      open: false,
      step: "pick",
      inspecting: false,
      width: 0,
      height: 0,
      animated: false,
      removeBackground: false,
      canRemoveBackground: stickers.media.images.supportsBackgroundRemoval(),
      name: "",
      keywords: "",
      packs: [],
      choice: { kind: "new", name: "", scope: "personal" },
      canCreateWorkspacePack: false,
      allowed: false,
      canSave: false,
    });
    this.stickers = stickers;
    this.onDispose(stickers.subscribe(() => this.recompute()));
    this.onDispose(() => this.releasePreview());
    this.recompute();
  }

  private recompute(): void {
    const s = this.stickers.state;
    const ws = this.stickers.ws;
    const me = ws.state.cache?.members[ws.state.meId];
    const now = this.stickers.clock.now();
    const packs = me === undefined ? [] : s.packs.filter((p) => canEditPack(p, me, s.settings, now));
    let choice = this.state.choice;
    const chosen = choice.kind === "existing" ? choice.packId : undefined;
    if (chosen !== undefined && !packs.some((p) => p.id === chosen)) choice = { kind: "new", name: "", scope: "personal" };
    this.setState(this.withSave({ ...this.state, packs, choice, canCreateWorkspacePack: s.canManage, allowed: s.canCreate }));
  }

  private withSave(s: StickerCreatorState): StickerCreatorState {
    const packOk = s.choice.kind === "existing" || packNameProblem(s.choice.name) === null;
    return { ...s, canSave: s.allowed && s.step === "edit" && !s.inspecting && this.file !== undefined && stickerNameProblem(s.name) === null && packOk };
  }

  private set(patch: Partial<StickerCreatorState>): void {
    this.setState(this.withSave({ ...this.state, ...patch }));
  }

  /** Opens the creator (optionally with an image already picked or pasted). */
  open(file?: PickedFile, packId?: string): void {
    this.reset();
    const own = this.state.packs[0];
    const choice: PackChoice =
      packId !== undefined && this.state.packs.some((p) => p.id === packId)
        ? { kind: "existing", packId }
        : own !== undefined
          ? { kind: "existing", packId: own.id }
          : { kind: "new", name: "", scope: "personal" };
    this.set({ open: true, choice });
    void this.stickers.refresh();
    if (file !== undefined) void this.pickFile(file);
  }

  /** "Save as sticker" on an image in a message: loads it through the media pipeline. */
  async openFromUrl(url: string, name: string, mime: string): Promise<void> {
    this.open();
    this.set({ inspecting: true });
    const gen = this.generation;
    try {
      const file = await this.stickers.media.images.fromUrl(url, name, mime);
      if (gen !== this.generation) return;
      await this.pickFile(file);
    } catch {
      if (gen === this.generation) this.set({ inspecting: false, error: msg("stickers.create.unreadable") });
    }
  }

  close(): void {
    this.reset();
    this.set({ open: false });
  }

  private reset(): void {
    this.generation += 1;
    this.releasePreview();
    this.file = undefined;
    const { error: _e, previewUrl: _p, crop: _c, ...rest } = this.state;
    this.setState(this.withSave({ ...rest, step: "pick", inspecting: false, width: 0, height: 0, animated: false, removeBackground: false, name: "", keywords: "" }));
  }

  private releasePreview(): void {
    if (this.state.previewUrl !== undefined) this.stickers.media.images.release(this.state.previewUrl);
  }

  /** An image was picked, dropped or pasted. */
  async pickFile(file: PickedFile): Promise<void> {
    const gen = ++this.generation;
    this.releasePreview();
    const { error: _e, previewUrl: _p, crop: _c, ...rest } = this.state;
    this.setState(this.withSave({ ...rest, inspecting: true }));
    try {
      const info = await this.stickers.media.images.inspect(file);
      if (gen !== this.generation) {
        this.stickers.media.images.release(info.previewUrl);
        return;
      }
      if (info.animated) {
        const problem = stickerFileProblem({ mime: file.mime, size: file.size, width: info.width, height: info.height }, true);
        if (problem !== null) {
          this.stickers.media.images.release(info.previewUrl);
          this.set({ inspecting: false, error: problem, step: "pick" });
          return;
        }
      }
      this.file = file;
      const base = file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim().slice(0, 40);
      this.set({
        inspecting: false,
        step: "edit",
        previewUrl: info.previewUrl,
        width: info.width,
        height: info.height,
        animated: info.animated,
        removeBackground: false,
        name: this.state.name === "" ? base : this.state.name,
      });
    } catch {
      if (gen === this.generation) this.set({ inspecting: false, step: "pick", error: msg("stickers.create.unreadable") });
    }
  }

  setCrop(crop: CropArea): void {
    this.set({ crop });
  }

  setRemoveBackground(on: boolean): void {
    if (this.state.canRemoveBackground && !this.state.animated) this.set({ removeBackground: on });
  }

  setName(name: string): void {
    this.set({ name: name.slice(0, 60) });
  }

  setKeywords(keywords: string): void {
    this.set({ keywords });
  }

  choosePack(packId: string): void {
    this.set({ choice: { kind: "existing", packId } });
  }

  chooseNewPack(): void {
    if (this.state.choice.kind === "new") return;
    this.set({ choice: { kind: "new", name: "", scope: "personal" } });
  }

  setNewPackName(name: string): void {
    const c = this.state.choice;
    if (c.kind === "new") this.set({ choice: { ...c, name: name.slice(0, 60) } });
  }

  setNewPackScope(scope: StickerPackScope): void {
    const c = this.state.choice;
    if (c.kind === "new" && (scope === "personal" || this.state.canCreateWorkspacePack)) this.set({ choice: { ...c, scope } });
  }

  /** Makes the image, uploads it and creates the sticker (and the new pack). Resolves true when saved. */
  async save(): Promise<boolean> {
    const s = this.state;
    const file = this.file;
    if (!s.canSave || file === undefined) {
      const problem = stickerNameProblem(s.name) ?? (s.choice.kind === "new" ? packNameProblem(s.choice.name) : null);
      if (problem !== null) this.set({ error: problem });
      return false;
    }
    const gen = this.generation;
    this.set({ step: "saving" });
    const { error: _e, ...cleared } = this.state;
    this.setState(cleared);
    const ws = this.stickers.ws;
    try {
      const options: { crop?: CropArea; removeBackground: boolean } = { removeBackground: s.removeBackground };
      if (s.crop !== undefined && !s.animated) options.crop = s.crop;
      const out = s.animated ? file : await this.stickers.media.images.render(file, options);
      const problem = stickerFileProblem({ mime: out.mime, size: out.size }, s.animated);
      if (problem !== null) throw new Problem(problem);
      let packId: string;
      if (s.choice.kind === "existing") packId = s.choice.packId;
      else {
        const pack = await this.stickers.createPack(s.choice.name, s.choice.scope);
        if (pack === undefined) throw new Problem(msg("stickers.notice.failed"));
        packId = pack.id;
        this.set({ choice: { kind: "existing", packId } });
      }
      const upload = ws.deps.data.uploadFile(ws.wsId, out, { type: "sticker" });
      const stored = await upload.done;
      const req: { fileId: string; name: string; keywords: string[]; animated?: boolean; clientId: string } = {
        fileId: stored.id,
        name: s.name.trim(),
        keywords: normalizeKeywords(s.keywords),
        clientId: upload.id,
      };
      if (s.animated) req.animated = true;
      const sticker = await this.stickers.media.api.createSticker(ws.wsId, packId, req);
      if (gen !== this.generation) return true;
      this.stickers.stickerCreated(sticker);
      this.close();
      return true;
    } catch (e) {
      if (gen !== this.generation) return false;
      const error =
        e instanceof Problem
          ? e.descriptor
          : errorKind(e) === "network"
            ? msg("stickers.notice.offline")
            : errorKind(e) === "forbidden"
              ? msg("stickers.notice.forbidden")
              : errorMessage(e).startsWith("stickers.")
                ? msg("stickers.create.unreadable")
                : msg("stickers.notice.failed");
      this.set({ step: "edit", error });
      return false;
    }
  }

  dismissError(): void {
    if (this.state.error === undefined) return;
    const { error: _e, ...rest } = this.state;
    this.setState(this.withSave(rest));
  }
}

class Problem extends Error {
  constructor(readonly descriptor: MessageDescriptor) {
    super(descriptor.key);
  }
}
